/**
 * CategoryEnvelopeContext - Single Source of Truth Provider
 * ==========================================================
 * FINLIST SSOT: Unified category-envelope atomic wiring (global-scale perf target)
 * 
 * This context provides a SINGLE SOURCE OF TRUTH for categories and envelopes.
 * All pages MUST use this context for category/envelope data.
 * 
 * GUARDRAILS:
 * - 🔥 No envelope/category creation outside budgetService or unified backend endpoint
 * - 🔥 No client-side envelope total mutations - backend atomic writes only
 * - 🔥 One-Envelope-Per-Category rule is enforced
 * 
 * Architecture:
 * - Categories: `users/{uid}/categories/{categoryId}`
 * - Envelopes: `users/{uid}/budgets/{budgetId}/envelopes/{envelopeId}`
 * - Transactions ALWAYS include `categoryId` and `envelopeId`
 * 
 * @version 1.0.0
 * @date 2025-11-28
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  getCategories,
  getCategoryById,
  subscribeToCategoryChanges,
  mapLegacyToCanonicalCategoryId,
  CATEGORY_TYPES
} from '@/services/categoryRegistryService';
import {
  getCanonicalEnvelopes,
  findEnvelopeByCategory,
  // FINLIST ENVELOPE SYSTEM: createEnvelopeForCategory removed from context imports
  // Only Budget page can create envelopes. See budgetService.js for envelope creation.
  subscribeToEnvelopeChanges,
  getActiveBudget
} from '@/services/budgetService';
import logger from '@/utils/logger';

// Debug logging - only in dev with VITE_DEBUG_SSOT=true
const DEBUG_SSOT = import.meta.env.DEV && import.meta.env.VITE_DEBUG_SSOT === 'true';

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const CategoryEnvelopeContext = createContext(null);

/**
 * Hook to access Category-Envelope context
 * FINLIST SSOT: All components MUST use this hook for category/envelope data
 */
export function useCategoryEnvelope() {
  const context = useContext(CategoryEnvelopeContext);
  if (!context) {
    throw new Error('useCategoryEnvelope must be used within a CategoryEnvelopeProvider');
  }
  return context;
}

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

/**
 * CategoryEnvelopeProvider
 * FINLIST SSOT: Wrap your app with this provider at the root level
 */
export function CategoryEnvelopeProvider({ children }) {
  const { user, loading } = useAuth();
  
  // State
  const [categories, setCategories] = useState([]);
  const [envelopes, setEnvelopes] = useState([]);
  const [categoryToEnvelopeMap, setCategoryToEnvelopeMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentBudgetId, setCurrentBudgetId] = useState(null);

  // ============================================================================
  // DATA LOADING & SUBSCRIPTIONS
  // ============================================================================

  /**
   * Load initial data and set up real-time subscriptions
   */
  useEffect(() => {
    if (loading) return;
    if (!user?.uid) {
      setCategories([]);
      setEnvelopes([]);
      setCategoryToEnvelopeMap({});
      setIsLoading(false);
      return;
    }

    const userId = user.uid;
    let unsubscribeCategories = () => {};
    let unsubscribeEnvelopes = () => {};

    const initializeData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (DEBUG_SSOT) console.log('[SSOT CONTEXT] Initializing data for user:', userId);

        // Subscribe to category changes
        unsubscribeCategories = subscribeToCategoryChanges(
          userId,
          (updatedCategories) => {
            if (DEBUG_SSOT) console.log('[SSOT CONTEXT] Categories updated:', updatedCategories.length);
            setCategories(updatedCategories);
          },
          { activeOnly: true }
        );

        // Get current budget first, then fetch envelopes
        // Note: budgetService determines the current budget
        if (DEBUG_SSOT) console.log('[SSOT CONTEXT] Fetching active budget...');
        const activeBudget = await getActiveBudget(userId);
        if (DEBUG_SSOT) console.log('[SSOT CONTEXT] Active budget result:', activeBudget?.id || 'NOT FOUND', activeBudget?.name || '');
        
        let fetchedBudgetId = null;
        
        if (activeBudget?.id) {
          fetchedBudgetId = activeBudget.id;
          setCurrentBudgetId(activeBudget.id);
          if (DEBUG_SSOT) console.log('[SSOT CONTEXT] Fetching envelopes for budget:', activeBudget.id);
          const envelopesData = await getCanonicalEnvelopes(userId, activeBudget.id);
          if (DEBUG_SSOT) console.log('[SSOT CONTEXT] Initial envelopes loaded:', envelopesData?.length || 0);
          setEnvelopes(envelopesData || []);
        } else {
          if (DEBUG_SSOT) console.log('[SSOT CONTEXT] No active budget found for user - cannot load envelopes');
          setEnvelopes([]);
        }

        // Subscribe to envelope changes - PASS THE BUDGET ID if we have it
        // This ensures the subscription targets the correct budget
        if (typeof subscribeToEnvelopeChanges === 'function') {
          if (DEBUG_SSOT) console.log('[SSOT CONTEXT] Setting up envelope subscription with budgetId:', fetchedBudgetId || 'auto-detect');
          unsubscribeEnvelopes = subscribeToEnvelopeChanges(
            userId,
            (updatedEnvelopes) => {
              if (DEBUG_SSOT) console.log('[SSOT CONTEXT] Envelopes updated via subscription:', updatedEnvelopes.length);
              setEnvelopes(updatedEnvelopes);
            },
            fetchedBudgetId // Pass the budget ID to avoid double-fetching
          );
        }

        setIsLoading(false);
        if (DEBUG_SSOT) console.log('[SSOT CONTEXT] Initialization complete');
      } catch (err) {
        console.error('[SSOT CONTEXT] Initialization error:', err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    initializeData();

    return () => {
      unsubscribeCategories();
      unsubscribeEnvelopes();
    };
  }, [user, loading]);

  // ============================================================================
  // VISIBILITY CHANGE HANDLER - Refresh on tab focus
  // ============================================================================
  
  /**
   * Refresh envelope data when user returns to the tab
   * This ensures stale data is cleared after data deletion
   */
  useEffect(() => {
    if (!user?.uid || loading) return;
    
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('[SSOT CONTEXT] Tab visible - refreshing envelope data');
        try {
          const activeBudget = await getActiveBudget(user.uid);
          
          if (!activeBudget?.id) {
            // No active budget means envelopes should be empty
            console.log('[SSOT CONTEXT] No active budget found - clearing envelopes');
            setEnvelopes([]);
            setCurrentBudgetId(null);
            return;
          }
          
          // If budget ID changed, update and refetch
          if (activeBudget.id !== currentBudgetId) {
            console.log('[SSOT CONTEXT] Budget ID changed, refetching envelopes');
            setCurrentBudgetId(activeBudget.id);
          }
          
          // Refetch envelopes to ensure we have latest data
          const envelopesData = await getCanonicalEnvelopes(user.uid, activeBudget.id);
          console.log('[SSOT CONTEXT] Refreshed envelopes:', envelopesData?.length || 0);
          setEnvelopes(envelopesData || []);
        } catch (err) {
          console.error('[SSOT CONTEXT] Visibility refresh error:', err);
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, loading, currentBudgetId]);

  // ============================================================================
  // BUILD CATEGORY-TO-ENVELOPE MAPPING
  // ============================================================================

  /**
   * Build and maintain the category-to-envelope mapping
   * FINLIST SSOT: This ensures one envelope per category
   */
  useEffect(() => {
    if (!categories.length) {
      setCategoryToEnvelopeMap({});
      return;
    }

    const newMap = {};
    
    for (const category of categories) {
      // Find existing envelope for this category
      const matchedEnvelope = envelopes.find(env => {
        // Match by categoryId (canonical or linked)
        if (env.categoryId === category.id) return true;
        
        // FINLIST SSOT: Match by linkedCanonicalCategory if available
        if (env.linkedCanonicalCategory === category.id) return true;
        
        // Match by categoryKey (raw stored value)
        if (env.categoryKey === category.id) return true;
        
        // Match by legacy category mappings (e.g., cat_housing -> housing-rent)
        const canonicalFromLegacy = mapLegacyToCanonicalCategoryId(env.categoryId);
        if (canonicalFromLegacy === category.id) return true;
        
        // Match by name/slug (legacy compatibility)
        const envName = (env.name || '').toLowerCase();
        const catName = category.name.toLowerCase();
        const catSlug = (category.slug || '').toLowerCase();
        
        return envName === catName || envName === catSlug;
      });

      if (matchedEnvelope) {
        newMap[category.id] = matchedEnvelope.id;
      }
    }

    setCategoryToEnvelopeMap(newMap);
    if (DEBUG_SSOT) console.log('[SSOT CONTEXT] Category-Envelope map built:', Object.keys(newMap).length, 'mappings');
  }, [categories, envelopes]);

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Get envelope ID for a category
   * FINLIST SSOT: Use this for transaction creation
   */
  const getEnvelopeIdForCategory = useCallback((categoryId) => {
    if (!categoryId) return null;
    
    // Direct lookup in map
    if (categoryToEnvelopeMap[categoryId]) {
      return categoryToEnvelopeMap[categoryId];
    }

    // Try legacy mapping
    const canonicalId = mapLegacyToCanonicalCategoryId(categoryId);
    if (categoryToEnvelopeMap[canonicalId]) {
      return categoryToEnvelopeMap[canonicalId];
    }
    
    // FALLBACK: If categories haven't loaded yet but envelopes have,
    // try direct lookup in envelopes array by multiple fields
    // This handles the case where categories are empty (0) but envelopes exist
    if (envelopes.length > 0) {
      const directMatch = envelopes.find(env => 
        env.categoryId === categoryId || 
        env.categoryKey === categoryId ||
        env.linkedCanonicalCategory === categoryId ||
        env.categoryId === canonicalId ||
        env.categoryKey === canonicalId ||
        env.linkedCanonicalCategory === canonicalId ||
        // Also check reverse mapping (legacy to canonical)
        mapLegacyToCanonicalCategoryId(env.categoryId) === categoryId ||
        mapLegacyToCanonicalCategoryId(env.categoryKey) === categoryId
      );
      if (directMatch) {
        console.log(`[SSOT CONTEXT] Direct envelope match found for ${categoryId}: ${directMatch.id}`);
        return directMatch.id;
      }
    }
    
    return null;
  }, [categoryToEnvelopeMap, envelopes]);

  /**
   * Get envelope object for a category
   */
  const getEnvelopeForCategory = useCallback((categoryId) => {
    if (!categoryId) return null;
    
    // First try getting the envelope ID from the lookup function
    const envelopeId = getEnvelopeIdForCategory(categoryId);
    if (envelopeId) {
      return envelopes.find(env => env.id === envelopeId || env.envelopeId === envelopeId) || null;
    }
    
    // Direct fallback: try matching by categoryId/categoryKey directly
    const canonicalId = mapLegacyToCanonicalCategoryId(categoryId);
    return envelopes.find(env => 
      env.categoryId === categoryId || 
      env.categoryKey === categoryId ||
      env.categoryId === canonicalId ||
      env.categoryKey === canonicalId
    ) || null;
  }, [getEnvelopeIdForCategory, envelopes]);

  /**
   * Get category by ID
   */
  const getCategoryFromRegistry = useCallback((categoryId) => {
    if (!categoryId) return null;
    return categories.find(cat => cat.id === categoryId) || null;
  }, [categories]);

  /**
   * Get categories by type
   */
  const getCategoriesByType = useCallback((type) => {
    if (!type) return categories;
    return categories.filter(cat => cat.type === type);
  }, [categories]);

  /**
   * Normalize a category reference to canonical categoryId
   * FINLIST SSOT: Use this when receiving category data from any source
   */
  const normalizeToCanonicalCategoryId = useCallback((categoryRef) => {
    if (!categoryRef) return 'cat_other_expense';
    
    // Already canonical
    if (typeof categoryRef === 'string' && categoryRef.startsWith('cat_')) {
      return categoryRef;
    }
    
    // Map legacy reference
    return mapLegacyToCanonicalCategoryId(categoryRef);
  }, []);

  /**
   * Prepare transaction data with proper category and envelope IDs
   * FINLIST SSOT: Use this before creating any transaction
   */
  const prepareTransactionData = useCallback((transactionData) => {
    const categoryId = normalizeToCanonicalCategoryId(
      transactionData.categoryId || transactionData.category
    );
    const envelopeId = getEnvelopeIdForCategory(categoryId);
    const category = getCategoryFromRegistry(categoryId);

    return {
      ...transactionData,
      categoryId,
      envelopeId,
      categoryName: category?.name || 'Other',
      categoryType: category?.type || CATEGORY_TYPES.EXPENSE
    };
  }, [normalizeToCanonicalCategoryId, getEnvelopeIdForCategory, getCategoryFromRegistry]);

  /**
   * Validate that category-envelope link exists
   * FINLIST SSOT: Call this before transaction creation
   * 
   * GUARDRAIL: This function does NOT auto-create envelopes.
   * Only the Budget page can create envelopes. If no envelope exists,
   * user must first create it on the Budget page.
   */
  const validateCategoryEnvelopeLink = useCallback(async (categoryId) => {
    if (!categoryId || !user?.uid) {
      return { valid: false, error: 'Missing category or user' };
    }

    const canonicalId = normalizeToCanonicalCategoryId(categoryId);
    const category = getCategoryFromRegistry(canonicalId);
    
    if (!category) {
      return { valid: false, error: `Category not found: ${categoryId}` };
    }

    const envelopeId = getEnvelopeIdForCategory(canonicalId);

    // FINLIST ENVELOPE SYSTEM GUARDRAIL:
    // DO NOT auto-create envelopes here. Only Budget page can create envelopes.
    // If no envelope exists, return an error with guidance.
    if (!envelopeId) {
      console.log(`[SSOT CONTEXT] No envelope for category ${canonicalId}. User must create on Budget page.`);
      return { 
        valid: false, 
        error: `No budget envelope exists for "${category?.name || canonicalId}". Please create one on the Budget page first.`,
        missingEnvelope: true,
        categoryId: canonicalId,
        categoryName: category?.name
      };
    }

    return {
      valid: true,
      categoryId: canonicalId,
      envelopeId,
      category,
      envelope: envelopes.find(e => e.id === envelopeId)
    };
  }, [user, normalizeToCanonicalCategoryId, getCategoryFromRegistry, getEnvelopeIdForCategory, envelopes]);

  /**
   * Refresh all data
   */
  const refreshData = useCallback(async () => {
    if (!user?.uid) return;
    
    try {
      setIsLoading(true);
      // Get active budget first for envelope fetch
      const activeBudget = await getActiveBudget(user.uid);
      const budgetId = activeBudget?.id || currentBudgetId;
      
      const [categoriesData, envelopesData] = await Promise.all([
        getCategories(user.uid, { activeOnly: true }),
        budgetId ? getCanonicalEnvelopes(user.uid, budgetId) : Promise.resolve([])
      ]);
      
      if (activeBudget?.id) {
        setCurrentBudgetId(activeBudget.id);
      } else {
        setCurrentBudgetId(null);
      }
      setCategories(categoriesData || []);
      setEnvelopes(envelopesData || []);
      setIsLoading(false);
    } catch (err) {
      console.error('[SSOT CONTEXT] Refresh error:', err);
      setError(err.message);
      setIsLoading(false);
    }
  }, [user, currentBudgetId]);

  /**
   * Clear all cached data immediately
   * MUST be called after "Delete All Data" or similar destructive operations
   * to ensure the UI reflects the empty state.
   */
  const clearCache = useCallback(() => {
    console.log('[SSOT CONTEXT] Clearing all cached data');
    setCategories([]);
    setEnvelopes([]);
    setCategoryToEnvelopeMap({});
    setCurrentBudgetId(null);
    setError(null);
  }, []);

  // ============================================================================
  // MEMOIZED CONTEXT VALUE
  // ============================================================================

  const contextValue = useMemo(() => ({
    // State
    categories,
    envelopes,
    categoryToEnvelopeMap,
    currentBudgetId,
    isLoading,
    error,
    
    // Categorized getters
    expenseCategories: categories.filter(c => c.type === CATEGORY_TYPES.EXPENSE),
    incomeCategories: categories.filter(c => c.type === CATEGORY_TYPES.INCOME),
    savingsCategories: categories.filter(c => c.type === CATEGORY_TYPES.SAVINGS),
    
    // Helper functions
    getEnvelopeIdForCategory,
    getEnvelopeForCategory,
    getCategoryFromRegistry,
    getCategoriesByType,
    normalizeToCanonicalCategoryId,
    prepareTransactionData,
    validateCategoryEnvelopeLink,
    refreshData,
    refreshEnvelopes: refreshData, // Alias for backward compatibility with BudgetPage
    clearCache,
    
    // Constants
    CATEGORY_TYPES
  }), [
    categories,
    envelopes,
    categoryToEnvelopeMap,
    currentBudgetId,
    isLoading,
    error,
    getEnvelopeIdForCategory,
    getEnvelopeForCategory,
    getCategoryFromRegistry,
    getCategoriesByType,
    normalizeToCanonicalCategoryId,
    prepareTransactionData,
    validateCategoryEnvelopeLink,
    refreshData,
    clearCache
  ]);

  return (
    <CategoryEnvelopeContext.Provider value={contextValue}>
      {children}
    </CategoryEnvelopeContext.Provider>
  );
}

// ============================================================================
// CONVENIENCE HOOKS
// ============================================================================

/**
 * Hook to get expense categories only
 */
export function useExpenseCategories() {
  const { expenseCategories, isLoading } = useCategoryEnvelope();
  return { categories: expenseCategories, isLoading };
}

/**
 * Hook to get income categories only
 */
export function useIncomeCategories() {
  const { incomeCategories, isLoading } = useCategoryEnvelope();
  return { categories: incomeCategories, isLoading };
}

/**
 * Hook to prepare transaction with proper SSOT IDs
 */
export function useTransactionPreparation() {
  const { prepareTransactionData, validateCategoryEnvelopeLink } = useCategoryEnvelope();
  return { prepareTransactionData, validateCategoryEnvelopeLink };
}

// ============================================================================
// FINLIST ENVELOPE SYSTEM HOOKS (Phase 2)
// ============================================================================

/**
 * Hook to get all Budget envelopes
 * FINLIST ENVELOPE SYSTEM: Use this to display envelope options in dropdowns
 */
export function useBudgetEnvelopes() {
  const { envelopes, isLoading, currentBudgetId, refreshData } = useCategoryEnvelope();
  return { 
    envelopes, 
    isLoading, 
    budgetId: currentBudgetId,
    refresh: refreshData 
  };
}

/**
 * Hook to search envelopes by name
 * FINLIST ENVELOPE SYSTEM: Use for envelope search (first 2+ characters)
 * @returns {Function} searchEnvelopes - Function that takes a query and returns matching envelopes
 */
export function useEnvelopeSearch() {
  const { envelopes } = useCategoryEnvelope();
  
  const searchEnvelopes = (query) => {
    if (!query || query.length < 2) return [];
    const lowerQuery = query.toLowerCase();
    return envelopes.filter(env => 
      env.name?.toLowerCase().includes(lowerQuery) ||
      env.label?.toLowerCase().includes(lowerQuery) ||
      env.categoryKey?.toLowerCase().includes(lowerQuery)
    );
  };
  
  return { searchEnvelopes, allEnvelopes: envelopes };
}

/**
 * Hook to get envelope by ID
 * FINLIST ENVELOPE SYSTEM: Use to get envelope details for display
 */
export function useEnvelopeById(envelopeId) {
  const { envelopes } = useCategoryEnvelope();
  const envelope = envelopeId ? envelopes.find(e => e.id === envelopeId || e.envelopeId === envelopeId) : null;
  return { envelope };
}

/**
 * Hook to validate an envelope exists
 * FINLIST ENVELOPE SYSTEM: Use before creating transactions
 * Returns { valid, envelope, error } 
 */
export function useEnvelopeValidation() {
  const { envelopes } = useCategoryEnvelope();
  
  const validateEnvelope = (envelopeId) => {
    if (!envelopeId) {
      return { valid: false, envelope: null, error: 'No envelope ID provided' };
    }
    const envelope = envelopes.find(e => e.id === envelopeId || e.envelopeId === envelopeId);
    if (!envelope) {
      return { valid: false, envelope: null, error: 'Envelope not found. Please create it on the Budget page.' };
    }
    return { valid: true, envelope, error: null };
  };
  
  return { validateEnvelope };
}

export default CategoryEnvelopeContext;
